import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CoachMessageInput, { useCoachComposerSubmit } from '../CoachMessageInput';
import CoachAvatar from '../CoachAvatar';
import {
  DEFAULT_MEDIA_CHAT_EXPANDED,
  hiddenMediaCoachMessageCount,
  historyScrollTop,
  mediaCoachHistoryKey,
  mediaCoachMessageKey,
  mediaCoachPendingPresentation,
  uniqueMediaCoachMessages,
  visibleMediaCoachMessages,
} from './mediaCoachThread';

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 45;

const newRequestId = () => `mcc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * The owner's private Coach conversation for one media activity.
 *
 * Everything rendered here comes from a fresh canonical read; the WebSocket only
 * invalidates. Peers never mount it, and a client that cannot reach the media
 * Coach API renders nothing rather than an empty shell.
 */
function MediaCoachChat({
  activityId,
  backendService,
  readOnly = false,
  activityStatus = null,
  coachImageUrl = null,
  coachEmoji = '🥋',
  refreshKey = 0,
}) {
  const supported = !readOnly && typeof backendService?.getMediaCoachThread === 'function';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(supported));
  const [loadError, setLoadError] = useState('');
  const [expanded, setExpanded] = useState(DEFAULT_MEDIA_CHAT_EXPANDED);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [pollRevision, setPollRevision] = useState(0);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(null);
  const activityIdRef = useRef(activityId);
  activityIdRef.current = activityId;
  const historyRef = useRef(null);
  const pollsRef = useRef(0);
  const refreshMountedRef = useRef(false);
  // Only the newest read may apply. An activity switch or a socket-driven
  // refetch must never be overwritten by a slower earlier request.
  const readGenerationRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!supported) return;
    const generation = readGenerationRef.current + 1;
    readGenerationRef.current = generation;
    if (!silent) setLoading(true);
    try {
      const thread = await backendService.getMediaCoachThread(activityId);
      if (!mountedRef.current || generation !== readGenerationRef.current) return;
      setData(thread);
      setLoadError('');
      return true;
    } catch (error) {
      if (!mountedRef.current || generation !== readGenerationRef.current) return;
      setLoadError('Coach chat could not load. Check your connection and try again.');
      return false;
    } finally {
      if (mountedRef.current && generation === readGenerationRef.current) setLoading(false);
    }
  }, [activityId, backendService, supported]);

  // Held in a ref so a caller that hands down a fresh `backendService` object on
  // every render cannot turn the reset effect into a refetch loop.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    if (!supported) return undefined;
    setData(null);
    setExpanded(DEFAULT_MEDIA_CHAT_EXPANDED);
    setDraft('');
    setSending(false);
    setRetrying(false);
    setSendError('');
    requestIdRef.current = null;
    pollsRef.current = 0;
    setPollExhausted(false);
    setPollRevision(0);
    loadRef.current();
    return undefined;
  }, [activityId, supported]);

  useEffect(() => {
    if (!supported) return;
    if (!refreshMountedRef.current) {
      refreshMountedRef.current = true;
      return;
    }
    pollsRef.current = 0;
    setPollExhausted(false);
    setPollRevision(current => current + 1);
    loadRef.current({ silent: true });
  }, [refreshKey, supported]);

  const messages = useMemo(() => uniqueMediaCoachMessages(data?.messages || []), [data]);
  const pending = useMemo(() => mediaCoachPendingPresentation(data?.pending || []), [data]);
  const visible = useMemo(
    () => visibleMediaCoachMessages({ messages, expanded }),
    [messages, expanded]
  );
  const hiddenCount = hiddenMediaCoachMessageCount(messages);
  const pendingKey = pending ? `${pending.kind}:${pending.state}` : 'none';
  const historyKey = mediaCoachHistoryKey({ activityId, count: messages.length, pendingKey });

  // A bounded poll, not an open-ended timer: the socket is best effort, so an
  // unsettled generation is reconciled from the canonical thread and then stops.
  useEffect(() => {
    if (!supported) return undefined;
    if (!pending || pending.state === 'failed') {
      pollsRef.current = 0;
      setPollExhausted(false);
      return undefined;
    }
    if (pollExhausted || pollsRef.current >= MAX_POLLS) return undefined;
    const scheduledActivityId = activityId;
    const timer = setTimeout(async () => {
      pollsRef.current += 1;
      await loadRef.current({ silent: true });
      if (!mountedRef.current || activityIdRef.current !== scheduledActivityId) return;
      if (pollsRef.current >= MAX_POLLS) setPollExhausted(true);
      setPollRevision(current => current + 1);
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [activityId, supported, pending, historyKey, pollExhausted, pollRevision]);

  const manualRefresh = useCallback(async () => {
    pollsRef.current = 0;
    setPollExhausted(false);
    setPollRevision(current => current + 1);
    await load({ silent: Boolean(data) });
  }, [data, load]);

  // Scroll the nested history itself; `scrollIntoView` would move the page.
  useEffect(() => {
    const history = historyRef.current;
    if (!history || visible.length === 0) return undefined;
    const anchor = () => {
      if (historyRef.current) historyRef.current.scrollTop = historyScrollTop(historyRef.current);
    };
    anchor();
    const frame = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame(anchor)
      : null;
    return () => {
      if (frame && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
    };
  }, [historyKey, expanded, visible.length]);

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending || typeof backendService?.sendMediaCoachMessage !== 'function') return;
    // One request id per user turn, reused across resubmits so a retry after a
    // dropped connection cannot duplicate the message.
    if (!requestIdRef.current || requestIdRef.current.text !== text) {
      requestIdRef.current = { id: newRequestId(), text };
    }
    const submitted = requestIdRef.current;
    const submittedActivityId = activityId;
    setSending(true);
    setSendError('');
    try {
      await backendService.sendMediaCoachMessage(activityId, { message: text, request_id: submitted.id });
      if (!mountedRef.current || activityIdRef.current !== submittedActivityId) return;
      requestIdRef.current = null;
      setDraft('');
      await load({ silent: true });
    } catch (error) {
      if (mountedRef.current && activityIdRef.current === submittedActivityId) {
        setSendError('Coach could not send that message. Try again.');
      }
    } finally {
      if (mountedRef.current && activityIdRef.current === submittedActivityId) setSending(false);
    }
  }, [activityId, backendService, draft, load, sending]);

  const retryGeneration = useCallback(async () => {
    if (!pending?.requestId || retrying || typeof backendService?.retryMediaCoachRequest !== 'function') return;
    const submittedActivityId = activityId;
    setRetrying(true);
    try {
      await backendService.retryMediaCoachRequest(activityId, pending.requestId);
      if (!mountedRef.current || activityIdRef.current !== submittedActivityId) return;
      await load({ silent: true });
    } catch (error) {
      if (mountedRef.current && activityIdRef.current === submittedActivityId) {
        setSendError('Coach could not restart that note. Try again.');
      }
    } finally {
      if (mountedRef.current && activityIdRef.current === submittedActivityId) setRetrying(false);
    }
  }, [activityId, backendService, load, pending, retrying]);

  const submitComposer = useCoachComposerSubmit(submit, sending || (loading && !data));

  if (!supported) return null;

  const showLoading = loading && !data;
  const composerDisabled = sending || showLoading;

  return (
    <div className={`media-coach-chat${expanded ? ' chat-expanded' : ''}`}>
      <div className="media-coach-header">
        <CoachAvatar
          imageUrl={coachImageUrl}
          emoji={coachEmoji}
          identitySettled
          alt="Coach"
          className="media-coach-header-avatar"
        />
        <span className="media-coach-header-title">Coach</span>
        {activityStatus === 'complete' && <span className="media-coach-header-note">Completed activity</span>}
      </div>

      {showLoading && (
        <div className="media-coach-status" role="status">Loading Coach chat…</div>
      )}

      {loadError && (
        <div className="media-coach-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="media-coach-retry" onClick={manualRefresh}>Try again</button>
        </div>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          className="media-coach-history-toggle"
          onClick={() => setExpanded(current => !current)}
        >
          {expanded ? 'Hide previous messages' : `Show previous messages (${hiddenCount})`}
        </button>
      )}

      {visible.length > 0 && (
        <div
          className={`media-coach-history${expanded ? ' media-coach-history-expanded' : ''}`}
          ref={historyRef}
        >
          {visible.map((message, index) => (
            <div
              key={mediaCoachMessageKey(message, index)}
              className={`media-coach-message media-coach-message-${message.role === 'user' ? 'user' : 'coach'}`}
            >
              {message.role === 'user' ? (
                <>
                  <span className="media-coach-text">{message.text}</span>
                  <span className="media-coach-avatar" aria-hidden="true">👤</span>
                </>
              ) : (
                <>
                  <CoachAvatar
                    imageUrl={coachImageUrl}
                    emoji={coachEmoji}
                    identitySettled
                    alt="Coach"
                    className="media-coach-avatar"
                  />
                  <span className="media-coach-text">{message.text}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {!showLoading && !loadError && visible.length === 0 && !pending && (
        <div className="media-coach-empty">
          Coach comments here once your photos have been analysed. You can ask about a photo, a metric or a retake.
        </div>
      )}

      {pending && pending.state !== 'failed' && !pollExhausted && (
        <div className="media-coach-pending" role="status" aria-label={pending.label}>
          <span>{pending.label}</span>
          <button type="button" className="media-coach-retry" aria-label="Check media Coach reply" onClick={manualRefresh}>Check again</button>
          <span className="media-coach-typing" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </div>
      )}

      {pending && pending.state !== 'failed' && pollExhausted && (
        <div className="media-coach-stopped" role="status">
          <span>Coach is taking longer than expected. You can check again without resending anything.</span>
          <button type="button" className="media-coach-retry" onClick={manualRefresh}>Check again</button>
        </div>
      )}

      {pending && pending.state === 'failed' && (
        <div className="media-coach-error" role="alert">
          <span>{pending.label}</span>
          {pending.selectedExternal && <span> The selected Coach could not complete this reply. Check the Coach connection and send a new question after recovery; this reply cannot use hosted Retry.</span>}
          {pending.retryable && (
            <button
              type="button"
              className="media-coach-retry"
              onClick={retryGeneration}
              disabled={retrying}
            >
              Try again
            </button>
          )}
        </div>
      )}

      {sendError && <div className="media-coach-error" role="alert">{sendError}</div>}

      <div className="media-coach-composer">
        <CoachMessageInput
          className="media-coach-input"
          value={draft}
          placeholder="Ask Coach about these photos…"
          aria-label="Message Coach about this media activity"
          disabled={composerDisabled}
          onChange={event => setDraft(event.target.value)}
          onSend={submitComposer}
        />
        <button
          type="button"
          className="media-coach-send"
          aria-label="Send message to Coach"
          onClick={submitComposer}
          disabled={composerDisabled || !draft.trim()}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

export default MediaCoachChat;
